import React, { useEffect, useMemo, useState } from "react";
import { ActivityIndicator, StyleSheet, Switch, TouchableOpacity, View } from "react-native";
import { Plus, Trash, X } from "@phosphor-icons/react";
import { Controller, useFieldArray, useForm, useWatch } from "react-hook-form";

import type { AiTutor, McqQuestion } from "../../../../graphql/generated/graphql";
import { useCreateMcqQuestionMutation, useUpdateMcqQuestionMutation } from "../../../../graphql/generated/graphql";
import { useToast } from "../../../../contexts/Toast/ToastProvider";
import { colors, radii, spacing } from "../../../../styles/design-system";
import CustomButton from "../../../common/general/CustomButton/CustomButton";
import CustomDropdown, { type CustomDropdownOption } from "../../../common/general/CustomDropDown/CustomDropdown";
import CustomModal from "../../../common/general/CustomModal/CustomModal";
import CustomText from "../../../common/general/CustomText/CustomText";
import CustomTextInput from "../../../common/general/CustomTextInput/CustomTextInput";

type McqQuestionEditorMode = "create" | "edit";

const DEFAULT_CHOICES = ["", "", "", ""];
const MIN_CHOICES = 2;

type McqQuestionEditorForm = {
  question: string;
  selectedTutorId: string;
  choices: string[];
  correctChoiceIndices: number[];
  allowMultipleCorrectAnswers: boolean;
  explanation: string;
  totalScore: string;
};

type McqQuestionEditorModalProps = {
  visible: boolean;
  mode: McqQuestionEditorMode;
  aiTutors: AiTutor[];
  selectedAiTutorId?: string | null;
  assessmentId?: string;
  question?: McqQuestion | null;
  nextOrder?: number;
  draftMode?: boolean;
  onDraftSave?: (question: {
    id?: string | null;
    aiTutorId: string;
    question: string;
    choices: string[];
    correctChoiceIndices: number[];
    explanation?: string | null;
    totalScore?: number | null;
    order?: number | null;
  }) => Promise<void> | void;
  onClose: () => void;
  onSaved: () => Promise<void> | void;
};

const optionLabel = (index: number) => String.fromCharCode(65 + index);

const McqQuestionEditorModal = ({
  visible,
  mode,
  aiTutors,
  selectedAiTutorId,
  assessmentId,
  question,
  nextOrder,
  draftMode = false,
  onDraftSave,
  onClose,
  onSaved,
}: McqQuestionEditorModalProps) => {
  const { showToast } = useToast();
  const [createMcqQuestion, { loading: isCreating }] = useCreateMcqQuestionMutation();
  const [updateMcqQuestion, { loading: isUpdating }] = useUpdateMcqQuestionMutation();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { control, reset, setValue, handleSubmit, getValues } = useForm<McqQuestionEditorForm>({
    defaultValues: {
      question: "",
      selectedTutorId: "",
      choices: DEFAULT_CHOICES,
      correctChoiceIndices: [0],
      allowMultipleCorrectAnswers: false,
      explanation: "",
      totalScore: "",
    },
  });

  const {
    fields: choiceFields,
    append: appendChoice,
    remove: removeChoice,
    replace: replaceChoices,
  } = useFieldArray({
    control,
    name: "choices" as never,
  });

  const questionText = useWatch({ control, name: "question" });
  const selectedTutorId = useWatch({ control, name: "selectedTutorId" });
  const choices = useWatch({ control, name: "choices" }) ?? [];
  const correctChoiceIndices = useWatch({ control, name: "correctChoiceIndices" }) ?? [];
  const allowMultipleCorrectAnswers = useWatch({ control, name: "allowMultipleCorrectAnswers" }) ?? false;
  const isEditMode = mode === "edit";
  const tutorOptions: CustomDropdownOption[] = useMemo(
    () =>
      aiTutors.map((tutor) => ({
        label: tutor.title ?? "Interactive Learning Experience",
        value: tutor.id,
      })),
    [aiTutors]
  );

  const canSubmit = useMemo(() => {
    const hasQuestion = Boolean(questionText?.trim());
    const hasSelectedTutor = isEditMode ? true : Boolean(selectedTutorId);
    const normalizedChoices = choices.map((choice) => choice?.trim());
    return (
      hasQuestion && hasSelectedTutor && normalizedChoices.length >= MIN_CHOICES && correctChoiceIndices.length > 0
    );
  }, [questionText, selectedTutorId, choices, correctChoiceIndices, isEditMode]);

  const isSubmitting = isCreating || isUpdating;
  const modalTitle = isEditMode ? "Edit Question" : "Add Question";
  const submitText = isEditMode ? "Save Changes" : "Add Question";

  useEffect(() => {
    if (!visible) {
      return;
    }

    if (isEditMode && question) {
      const indices = (question as { correctChoiceIndices?: number[] }).correctChoiceIndices ?? [];
      const validIndices = indices.filter(
        (i) => typeof i === "number" && Number.isInteger(i) && i >= 0 && i < (question.choices?.length ?? 0)
      );
      const mcqQuestionWithTutor = question as McqQuestion & {
        aiTutorId?: string | null;
        aiTutor?: { id?: string | null } | null;
      };
      reset({
        question: question.question ?? "",
        selectedTutorId: selectedAiTutorId ?? mcqQuestionWithTutor.aiTutorId ?? question.aiTutor?.id ?? "",
        choices: question.choices?.length ? [...question.choices] : DEFAULT_CHOICES,
        correctChoiceIndices: validIndices.length > 0 ? validIndices : [0],
        allowMultipleCorrectAnswers: validIndices.length > 1,
        explanation: question.explanation ?? "",
        totalScore: typeof question.totalScore === "number" ? String(question.totalScore) : "",
      });
      replaceChoices(question.choices?.length ? [...question.choices] : DEFAULT_CHOICES);
    } else {
      reset({
        question: "",
        selectedTutorId: selectedAiTutorId ?? aiTutors[0]?.id ?? "",
        choices: DEFAULT_CHOICES,
        correctChoiceIndices: [0],
        allowMultipleCorrectAnswers: false,
        explanation: "",
        totalScore: "",
      });
      replaceChoices(DEFAULT_CHOICES);
    }

    setErrorMessage(null);
  }, [visible, isEditMode, question, selectedAiTutorId, aiTutors, reset, replaceChoices]);

  const handleAddOption = () => {
    appendChoice("");
    setErrorMessage(null);
  };

  const handleRemoveOption = (index: number) => {
    if (choiceFields.length <= MIN_CHOICES) {
      return;
    }

    removeChoice(index);
    setErrorMessage(null);

    const currentIndices = getValues("correctChoiceIndices");
    const nextIndices = currentIndices.filter((i) => i !== index).map((i) => (i > index ? i - 1 : i));
    setValue("correctChoiceIndices", nextIndices.length > 0 ? nextIndices : []);
  };

  const handleCorrectChoiceToggle = (index: number) => {
    const currentIndices = getValues("correctChoiceIndices");
    const allowMultiple = getValues("allowMultipleCorrectAnswers");

    if (allowMultiple) {
      const isSelected = currentIndices.includes(index);
      const nextIndices = isSelected
        ? currentIndices.filter((i) => i !== index)
        : [...currentIndices, index].sort((a, b) => a - b);
      setValue("correctChoiceIndices", nextIndices);
    } else {
      setValue("correctChoiceIndices", [index]);
    }
    setErrorMessage(null);
  };

  const handleAllowMultipleChange = (value: boolean) => {
    if (!value) {
      const currentIndices = getValues("correctChoiceIndices");
      if (currentIndices.length > 1) {
        setValue("correctChoiceIndices", [currentIndices[0]!]);
      }
    }
  };

  const onSubmit = async (data: McqQuestionEditorForm) => {
    setErrorMessage(null);

    if (!draftMode && !assessmentId) {
      setErrorMessage("Assessment ID is missing. Please close and reopen the question bank.");
      return;
    }

    const normalizedQuestion = data.question.trim();
    const normalizedSelectedTutorId = String(data.selectedTutorId ?? "").trim();
    const mcqQuestionWithTutor = question as McqQuestion & {
      aiTutorId?: string | null;
      aiTutor?: { id?: string | null } | null;
    };
    const fallbackTutorId = mcqQuestionWithTutor?.aiTutorId ?? mcqQuestionWithTutor?.aiTutor?.id ?? "";
    const resolvedAiTutorId = normalizedSelectedTutorId || fallbackTutorId;
    const normalizedChoices = data.choices.map((choice) => choice.trim());
    const normalizedExplanation = data.explanation.trim();
    const sortedIndices = [...data.correctChoiceIndices].sort((a, b) => a - b);

    if (!normalizedQuestion) {
      setErrorMessage("Question is required.");
      return;
    }

    if (!isEditMode && !resolvedAiTutorId) {
      setErrorMessage("Please select an interactive learning experience.");
      return;
    }

    if (normalizedChoices.length < MIN_CHOICES || normalizedChoices.some((choice) => !choice)) {
      setErrorMessage("Please provide at least two non-empty answer options.");
      return;
    }

    if (
      sortedIndices.length === 0 ||
      sortedIndices.some((i) => !Number.isInteger(i) || i < 0 || i >= normalizedChoices.length)
    ) {
      setErrorMessage("Please select at least one correct answer.");
      return;
    }

    let normalizedTotalScore: number | undefined;
    if (data.totalScore.trim()) {
      const parsedTotalScore = Number(data.totalScore.trim());
      if (!Number.isFinite(parsedTotalScore) || parsedTotalScore <= 0) {
        setErrorMessage("Total score must be a positive number.");
        return;
      }
      normalizedTotalScore = parsedTotalScore;
    }

    try {
      if (draftMode) {
        await onDraftSave?.({
          id: question?.id ?? null,
          aiTutorId: resolvedAiTutorId,
          question: normalizedQuestion,
          choices: normalizedChoices,
          correctChoiceIndices: sortedIndices,
          explanation: normalizedExplanation || null,
          totalScore: normalizedTotalScore ?? null,
          order: isEditMode ? question?.order : nextOrder,
        });
        await onSaved();
        showToast({
          message: isEditMode ? "Question updated successfully." : "Question added successfully.",
          type: "success",
        });
        onClose();
        return;
      }

      if (isEditMode) {
        if (!question?.id) {
          setErrorMessage("Question ID is missing for edit.");
          return;
        }

        const { data: res } = await updateMcqQuestion({
          variables: {
            mcqQuestionId: question.id,
            assessmentId,
            input: {
              question: normalizedQuestion,
              choices: normalizedChoices,
              correctChoiceIndices: sortedIndices,
              explanation: normalizedExplanation,
              totalScore: normalizedTotalScore,
              order: question.order,
            },
          } as any,
        });

        const updateResponse = res?.updateMcqQuestion;
        if (!updateResponse?.success) {
          setErrorMessage(updateResponse?.message || "Failed to update question.");
          return;
        }
      } else {
        const { data: res } = await createMcqQuestion({
          variables: {
            input: {
              assessmentId,
              aiTutorId: resolvedAiTutorId || undefined,
              question: normalizedQuestion,
              choices: normalizedChoices,
              correctChoiceIndices: sortedIndices,
              explanation: normalizedExplanation,
              totalScore: normalizedTotalScore,
              order: nextOrder,
            },
          },
        });

        const createResponse = res?.createMcqQuestion;
        if (!createResponse?.success) {
          setErrorMessage(createResponse?.message || "Failed to create question.");
          return;
        }
      }

      await onSaved();

      showToast({
        message: isEditMode ? "Question updated successfully." : "Question added successfully.",
        type: "success",
      });
      onClose();
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : "Something went wrong. Please try again.");
    }
  };

  return (
    <CustomModal
      visible={visible}
      onRequestClose={onClose}
      ariaLabel={modalTitle}
      style={styles.modal}
      bodyStyle={styles.modalBody}>
      <View style={styles.container}>
        <View style={styles.headerRow}>
          <CustomText text={modalTitle} useTranslationText={false} size="xl" weight="bold" style={styles.modalTitle} />
          <TouchableOpacity
            style={styles.closeButton}
            onPress={onClose}
            accessibilityRole="button"
            aria-label="Close question editor modal">
            <X size={18} color={colors.grey[500]} />
          </TouchableOpacity>
        </View>

        <View style={styles.content}>
          <View style={styles.fieldGroup}>
            <CustomText text="Question" useTranslationText={false} weight="semiBold" style={styles.fieldLabel} />
            <View style={styles.inputWrap}>
              <Controller
                control={control}
                name="question"
                render={({ field: { value, onChange } }) => (
                  <CustomTextInput
                    value={value}
                    onChangeText={onChange}
                    placeholder="Enter your question..."
                    multiline={true}
                    numberOfLines={4}
                    style={styles.questionInput}
                  />
                )}
              />
            </View>
          </View>

          {!isEditMode && (
            <View style={styles.fieldGroup}>
              <CustomText
                text="Interactive Learning Experience"
                useTranslationText={false}
                weight="semiBold"
                style={styles.fieldLabel}
              />
              <Controller
                control={control}
                name="selectedTutorId"
                render={({ field: { value, onChange } }) => (
                  <CustomDropdown
                    options={tutorOptions}
                    value={value}
                    onChange={onChange}
                    placeholder="Select an ILE"
                    fullWidth={true}
                    containerStyle={styles.dropdown}
                    disabled={aiTutors.length === 0}
                  />
                )}
              />
            </View>
          )}

          <View style={styles.fieldGroup}>
            <View style={styles.answerHeaderRow}>
              <CustomText
                text="Answer Options"
                useTranslationText={false}
                weight="semiBold"
                style={styles.fieldLabel}
              />
              <CustomText text="Click to set correct answer" useTranslationText={false} style={styles.answerHint} />
            </View>

            <View style={styles.toggleRow}>
              <CustomText text="Allow multiple correct answers" useTranslationText={false} style={styles.toggleLabel} />
              <Controller
                control={control}
                name="allowMultipleCorrectAnswers"
                render={({ field: { value, onChange } }) => (
                  <Switch
                    value={value}
                    onValueChange={(v) => {
                      onChange(v);
                      handleAllowMultipleChange(v);
                    }}
                    accessibilityLabel="Allow multiple correct answers"
                  />
                )}
              />
            </View>

            <View style={styles.choiceRows}>
              {choiceFields.map((choiceField, index) => {
                const isCorrect = correctChoiceIndices.includes(index);

                return (
                  <View key={choiceField.id} style={styles.choiceRow}>
                    <TouchableOpacity
                      style={[styles.choiceTag, isCorrect && styles.choiceTagCorrect]}
                      onPress={() => handleCorrectChoiceToggle(index)}
                      accessibilityRole="button"
                      aria-label={`Mark option ${optionLabel(index)} as correct answer`}>
                      <CustomText
                        text={optionLabel(index)}
                        useTranslationText={false}
                        weight="semiBold"
                        style={[styles.choiceTagText, isCorrect && styles.choiceTagTextCorrect]}
                      />
                    </TouchableOpacity>

                    <View style={styles.choiceInputWrap}>
                      <Controller
                        control={control}
                        name={`choices.${index}`}
                        render={({ field: { onChange, onBlur, value } }) => (
                          <CustomTextInput
                            value={value ?? ""}
                            onChangeText={(inputValue) => {
                              onChange(inputValue);
                              setErrorMessage(null);
                            }}
                            onBlur={onBlur}
                            placeholder={`Option ${optionLabel(index)}`}
                          />
                        )}
                      />
                    </View>

                    <TouchableOpacity
                      style={[
                        styles.deleteOptionButton,
                        choiceFields.length <= MIN_CHOICES && styles.deleteOptionButtonDisabled,
                      ]}
                      onPress={() => handleRemoveOption(index)}
                      disabled={choiceFields.length <= MIN_CHOICES}
                      accessibilityRole="button"
                      aria-label={`Remove option ${optionLabel(index)}`}>
                      <Trash
                        size={16}
                        color={choiceFields.length <= MIN_CHOICES ? colors.grey[300] : colors.grey[500]}
                      />
                    </TouchableOpacity>
                  </View>
                );
              })}
            </View>

            <TouchableOpacity
              onPress={handleAddOption}
              style={styles.addOptionLink}
              accessibilityRole="button"
              aria-label="Add option">
              <Plus size={16} color={colors.brand[500]} />
              <CustomText text="Add Option" useTranslationText={false} style={styles.addOptionLinkText} />
            </TouchableOpacity>
          </View>

          <View style={styles.fieldGroup}>
            <CustomText
              text="Explanation (Optional)"
              useTranslationText={false}
              weight="semiBold"
              style={styles.fieldLabel}
            />
            <View style={styles.inputWrap}>
              <Controller
                control={control}
                name="explanation"
                render={({ field: { value, onChange } }) => (
                  <CustomTextInput
                    value={value}
                    onChangeText={onChange}
                    placeholder="Why is this answer correct?"
                    multiline={true}
                    numberOfLines={3}
                    style={styles.explanationInput}
                  />
                )}
              />
            </View>
          </View>

          <View style={styles.fieldGroup}>
            <CustomText
              text="Total Score (Optional)"
              useTranslationText={false}
              weight="semiBold"
              style={styles.fieldLabel}
            />
            <View style={styles.inputWrap}>
              <Controller
                control={control}
                name="totalScore"
                render={({ field: { value, onChange } }) => (
                  <CustomTextInput
                    value={value}
                    onChangeText={(inputValue) => {
                      onChange(inputValue);
                      setErrorMessage(null);
                    }}
                    placeholder="e.g. 1"
                    keyboardType="numeric"
                  />
                )}
              />
            </View>
          </View>
        </View>

        {!!errorMessage && <CustomText text={errorMessage} useTranslationText={false} style={styles.errorText} />}

        <View style={styles.footer}>
          <CustomButton
            text="Cancel"
            useTranslationText={false}
            styleType="primaryTransparentStroke"
            onPress={onClose}
            disabled={isSubmitting}
            textStyle={styles.cancelButtonText}
            style={styles.cancelButton}
          />
          {isSubmitting ? (
            <View style={styles.submitButtonLoading}>
              <ActivityIndicator size="small" color={colors.white} />
            </View>
          ) : (
            <CustomButton
              text={submitText}
              useTranslationText={false}
              size="s"
              onPress={handleSubmit(onSubmit)}
              styleType="primary"
              disabled={!canSubmit}
              textStyle={styles.submitButtonText}
              style={styles.submitButton}
            />
          )}
        </View>
      </View>
    </CustomModal>
  );
};

const styles = StyleSheet.create({
  modal: {
    maxWidth: 740,
    paddingHorizontal: 0,
    paddingVertical: 0,
    borderRadius: 18,
    overflow: "hidden",
  },
  modalBody: {
    marginTop: 0,
    flexGrow: 0,
  },
  container: {
    width: "100%",
  },
  headerRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: spacing.xl,
    paddingTop: spacing.lg,
    paddingBottom: spacing.md,
    backgroundColor: colors.white,
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
  },
  modalTitle: {
    color: colors.grey[900],
  },
  closeButton: {
    width: 28,
    height: 28,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: radii.md,
  },
  content: {
    paddingHorizontal: spacing.xl,
    paddingTop: spacing.md,
    paddingBottom: spacing.md,
    gap: spacing.md,
  },
  fieldGroup: {
    gap: spacing.xs,
  },
  dropdown: {
    width: "100%",
  },
  fieldLabel: {
    color: colors.grey[700],
  },
  inputWrap: {
    width: "100%",
  },
  questionInput: {
    minHeight: 96,
    textAlignVertical: "top",
    borderRadius: radii.lg,
  },
  explanationInput: {
    minHeight: 92,
    textAlignVertical: "top",
    borderRadius: radii.lg,
  },
  answerHeaderRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-end",
    gap: spacing.sm,
    flexWrap: "wrap",
  },
  answerHint: {
    color: colors.grey[500],
    fontSize: 14,
  },
  toggleRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: 12,
    paddingVertical: 8,
  },
  toggleLabel: {
    color: "#344054",
    fontSize: 14,
  },
  choiceRows: {
    gap: spacing.sm,
  },
  choiceRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: spacing.xs,
  },
  choiceTag: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: colors.grey[50],
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 1,
    borderColor: colors.grey[200],
  },
  choiceTagCorrect: {
    backgroundColor: colors.success[50],
    borderColor: colors.success[200],
  },
  choiceTagText: {
    color: colors.grey[500],
  },
  choiceTagTextCorrect: {
    color: colors.success[600],
  },
  choiceInputWrap: {
    flex: 1,
  },
  deleteOptionButton: {
    width: 30,
    height: 30,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: radii.md,
  },
  deleteOptionButtonDisabled: {
    opacity: 0.5,
  },
  addOptionLink: {
    flexDirection: "row",
    alignItems: "center",
    alignSelf: "flex-start",
    gap: spacing.xs,
    paddingVertical: spacing.xxs,
    paddingHorizontal: 0,
    marginTop: 2,
  },
  addOptionLinkText: {
    color: colors.brand[500],
    fontSize: 14,
    fontWeight: "600",
  },
  errorText: {
    color: colors.error[500],
    paddingHorizontal: spacing.xl,
    paddingBottom: spacing.xs,
  },
  footer: {
    paddingHorizontal: spacing.xl,
    paddingTop: spacing.sm,
    paddingBottom: 22,
    borderTopWidth: 1,
    borderTopColor: colors.grey[200],
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  cancelButton: {
    minWidth: 84,
    justifyContent: "center",
  },
  cancelButtonText: {
    color: colors.grey[600],
    fontWeight: "600",
  },
  submitButton: {
    minWidth: 138,
    justifyContent: "center",
  },
  submitButtonText: {
    fontWeight: "700",
  },
  submitButtonLoading: {
    minWidth: 138,
    height: 36,
    borderRadius: radii.md,
    backgroundColor: colors.brand[500],
    alignItems: "center",
    justifyContent: "center",
  },
});

export default McqQuestionEditorModal;
